export const calculateAgeInDays = (dateOfBirth, measurementDate) => {
  const dob = new Date(dateOfBirth);
  const measureDate = new Date(measurementDate);
  const diffTime = Math.abs(measureDate - dob);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

export const calculateAgeInYears = (dateOfBirth, measurementDate) => {
  return calculateAgeInDays(dateOfBirth, measurementDate) / 365.25;
};

export const calculateGrowthVelocity = (measurements, dateOfBirth) => {
  if (!measurements || measurements?.length < 2) {
    return { heightVsAge: [], velocityVsAge: [], dataQualityFlags: [], suspiciousIntervals: [] };
  }

  const sorted = [...measurements]?.sort((a, b) => 
    new Date(a?.measurementDatetime) - new Date(b?.measurementDatetime)
  );

  const aggregated = aggregateRepeats(sorted);

  const heightVsAge = aggregated?.map(m => ({
    ageInDays: calculateAgeInDays(dateOfBirth, m?.measurementDatetime),
    ageInYears: calculateAgeInYears(dateOfBirth, m?.measurementDatetime),
    heightCm: m?.standingHeightCm,
    date: m?.measurementDatetime,
    replicateDifference: m?.replicateDifference
  }));

  const velocityVsAge = [];
  const dataQualityFlags = [];
  const suspiciousIntervals = [];

  for (let i = 0; i < heightVsAge?.length - 1; i++) {
    const current = heightVsAge?.[i];
    const next = heightVsAge?.[i + 1];

    const heightDiff = next?.heightCm - current?.heightCm;
    const daysDiff = next?.ageInDays - current?.ageInDays;

    if (daysDiff === 0) continue;

    const velocityCmPerYear = (heightDiff / daysDiff) * 365.25;
    const midpointAge = (current?.ageInYears + next?.ageInYears) / 2;

    let isSuspicious = false;
    const suspiciousReasons = [];

    // Rule 1: Negative height change greater than 0.3 cm
    if (heightDiff < -0.3) {
      isSuspicious = true;
      suspiciousReasons?.push('Negative height change > 0.3 cm');
      dataQualityFlags?.push({
        type: 'negative_growth',
        message: 'Height decreased between measurements - check measurement accuracy',
        ageInYears: midpointAge
      });
    }

    // Rule 2: Height increase greater than 2.0 cm in 30 days
    if (daysDiff <= 30 && heightDiff > 2.0) {
      isSuspicious = true;
      suspiciousReasons?.push('Height increase > 2.0 cm in 30 days');
      dataQualityFlags?.push({
        type: 'excessive_velocity',
        message: 'Height increase greater than 2.0 cm in 30 days - likely measurement error',
        ageInYears: midpointAge
      });
    }

    // Rule 4: Interval shorter than 14 days should be downweighted
    if (daysDiff < 14) {
      dataQualityFlags?.push({
        type: 'short_interval',
        message: 'Measurements too close together for reliable velocity calculation',
        ageInYears: midpointAge
      });
    }

    if (isSuspicious) {
      suspiciousIntervals?.push({
        fromAge: current?.ageInYears,
        toAge: next?.ageInYears,
        reasons: suspiciousReasons,
        intervalDays: daysDiff
      });
    }

    velocityVsAge?.push({
      ageInYears: midpointAge,
      velocityCmPerYear: velocityCmPerYear,
      intervalDays: daysDiff,
      isSuspicious: isSuspicious,
      weight: daysDiff < 14 ? 0.5 : 1.0
    });
  }

  return { heightVsAge, velocityVsAge, dataQualityFlags, suspiciousIntervals };
};

const aggregateRepeats = (measurements) => {
  const aggregated = [];
  let i = 0;

  while (i < measurements?.length) {
    const current = measurements?.[i];
    const repeats = [current];
    const currentDate = new Date(current?.measurementDatetime);

    for (let j = i + 1; j < measurements?.length; j++) {
      const next = measurements?.[j];
      const nextDate = new Date(next?.measurementDatetime);
      const daysDiff = Math.abs((nextDate - currentDate) / (1000 * 60 * 60 * 24));

      if (daysDiff <= 3) {
        repeats?.push(next);
      } else {
        break;
      }
    }

    const meanHeight = repeats?.reduce((sum, m) => sum + parseFloat(m?.standingHeightCm), 0) / repeats?.length;
    
    // Rule 3: Replicate difference greater than 0.5 cm
    let replicateDifference = null;
    if (repeats?.length === 2) {
      replicateDifference = Math.abs(parseFloat(repeats?.[0]?.standingHeightCm) - parseFloat(repeats?.[1]?.standingHeightCm));
    }

    aggregated?.push({
      ...current,
      standingHeightCm: meanHeight,
      repeatCount: repeats?.length,
      replicateDifference: replicateDifference
    });

    i += repeats?.length;
  }

  return aggregated;
};

export const detectPHV = (velocityData, sexAtBirth, currentAge) => {
  const candidateWindow = sexAtBirth === 'female' 
    ? { min: 8, max: 16 } 
    : { min: 10, max: 18 };

  if (currentAge < candidateWindow?.min || currentAge > candidateWindow?.max) {
    return {
      status: 'not_assessed',
      message: `PHV detection not assessed for this age range. Assessment window: ${candidateWindow?.min}-${candidateWindow?.max} years for ${sexAtBirth}s.`,
      candidateWindow
    };
  }

  const candidateVelocities = velocityData?.filter(
    v => v?.ageInYears >= candidateWindow?.min && v?.ageInYears <= candidateWindow?.max
  );

  if (candidateVelocities?.length < 3) {
    return {
      status: 'insufficient_data',
      message: 'Insufficient measurements within the candidate window for PHV detection. At least 3 measurements needed.',
      candidateWindow
    };
  }

  const smoothedVelocities = simpleMovingAverage(candidateVelocities, 3);

  let maxVelocity = -Infinity;
  let phvAge = null;

  smoothedVelocities?.forEach(v => {
    if (v?.smoothedVelocity > maxVelocity) {
      maxVelocity = v?.smoothedVelocity;
      phvAge = v?.ageInYears;
    }
  });

  if (!phvAge) {
    return {
      status: 'not_detected',
      message: 'PHV could not be detected with current data quality.',
      candidateWindow
    };
  }

  let uncertaintyYears = 0.5;

  return {
    status: 'detected',
    phvAge: phvAge,
    phvAgeRange: {
      earliest: phvAge - uncertaintyYears,
      latest: phvAge + uncertaintyYears
    },
    peakVelocity: maxVelocity,
    confidence: candidateVelocities?.length >= 6 ? 'high' : 'moderate',
    message: `Peak height velocity detected at approximately ${phvAge?.toFixed(1)} years (range: ${(phvAge - uncertaintyYears)?.toFixed(1)}-${(phvAge + uncertaintyYears)?.toFixed(1)} years)`,
    candidateWindow
  };
};

const simpleMovingAverage = (data, windowSize) => {
  const smoothed = [];

  for (let i = 0; i < data?.length; i++) {
    const start = Math.max(0, i - Math.floor(windowSize / 2));
    const end = Math.min(data?.length, i + Math.ceil(windowSize / 2));
    const window = data?.slice(start, end);
    const avg = window?.reduce((sum, v) => sum + v?.velocityCmPerYear, 0) / window?.length;

    smoothed?.push({
      ageInYears: data?.[i]?.ageInYears,
      originalVelocity: data?.[i]?.velocityCmPerYear,
      smoothedVelocity: avg
    });
  }

  return smoothed;
};

export const assessDataQuality = (measurements, dataQualityFlags) => {
  if (!measurements || measurements?.length === 0) {
    return { grade: 'F', confidence: 'none', message: 'No measurements available' };
  }

  if (measurements?.length < 3) {
    return { grade: 'D', confidence: 'low', message: 'Insufficient measurements for reliable analysis. At least 3 measurements recommended.' };
  }

  const criticalFlags = dataQualityFlags?.filter(f => f?.type === 'negative_growth' || f?.type === 'excessive_velocity')?.length;
  const warningFlags = dataQualityFlags?.filter(f => f?.type === 'short_interval')?.length;

  if (criticalFlags > measurements?.length * 0.3) {
    return { grade: 'D', confidence: 'low', message: 'Multiple data quality issues detected. Review measurement technique.' };
  }

  if (criticalFlags > 0 || warningFlags > measurements?.length * 0.5) {
    return { grade: 'C', confidence: 'moderate', message: 'Some data quality concerns. Results should be interpreted with caution.' };
  }

  if (measurements?.length >= 6 && warningFlags === 0) {
    return { grade: 'A', confidence: 'high', message: 'Excellent data quality. Results are reliable.' };
  }

  return { grade: 'B', confidence: 'good', message: 'Good data quality. Results are generally reliable.' };
};

export const assessDataQualityWithHardRequirements = (measurements, dateOfBirth, sexAtBirth, suspiciousIntervals) => {
  if (!measurements || measurements?.length === 0) {
    return { 
      grade: 'D', 
      meetsHardRequirements: false,
      confidence: 'insufficient', 
      message: 'No measurements available',
      improvements: ['Add measurements to begin tracking growth']
    };
  }

  const sorted = [...measurements]?.sort((a, b) => 
    new Date(a?.measurementDatetime) - new Date(b?.measurementDatetime)
  );

  const firstDate = new Date(sorted?.[0]?.measurementDatetime);
  const lastDate = new Date(sorted?.[sorted?.length - 1]?.measurementDatetime);
  const spanMonths = (lastDate - firstDate) / (1000 * 60 * 60 * 24 * 30.44);

  const currentAge = calculateAgeInYears(dateOfBirth, new Date());
  const candidateWindow = sexAtBirth === 'female' ? { min: 8, max: 16 } : { min: 10, max: 18 };
  
  const measurementsInWindow = measurements?.filter(m => {
    const age = calculateAgeInYears(dateOfBirth, m?.measurementDatetime);
    return age >= candidateWindow?.min && age <= candidateWindow?.max;
  });

  // Calculate median gap
  const gaps = [];
  for (let i = 0; i < sorted?.length - 1; i++) {
    const gap = (new Date(sorted?.[i + 1]?.measurementDatetime) - new Date(sorted?.[i]?.measurementDatetime)) / (1000 * 60 * 60 * 24);
    gaps?.push(gap);
  }
  const medianGap = gaps?.length > 0 ? gaps?.sort((a, b) => a - b)?.[Math.floor(gaps?.length / 2)] : 0;

  const suspiciousPercentage = suspiciousIntervals?.length / Math.max(measurements?.length - 1, 1);

  // Check replicate differences
  const replicateMeasurements = measurements?.filter(m => m?.heightReplicate1 && m?.heightReplicate2);
  const highReplicateDiffs = replicateMeasurements?.filter(m => {
    const diff = Math.abs(parseFloat(m?.heightReplicate1) - parseFloat(m?.heightReplicate2));
    return diff > 0.5;
  })?.length;

  const lowReplicateDiffs = replicateMeasurements?.filter(m => {
    const diff = Math.abs(parseFloat(m?.heightReplicate1) - parseFloat(m?.heightReplicate2));
    return diff <= 0.3;
  })?.length;

  // HARD REQUIREMENTS
  const hardReq1 = measurements?.length >= 8 && spanMonths >= 10;
  const hardReq2 = measurementsInWindow?.length >= 4;
  const hardReq3 = medianGap <= 45;
  const hardReq4 = suspiciousPercentage <= 0.20;

  const meetsHardRequirements = hardReq1 && hardReq2 && hardReq3 && hardReq4;

  const improvements = [];
  if (!hardReq1) {
    improvements?.push(`Need ${8 - measurements?.length} more measurements spanning ${Math.ceil(10 - spanMonths)} more months`);
  }
  if (!hardReq2) {
    improvements?.push(`Need ${4 - measurementsInWindow?.length} more measurements within candidate age window (${candidateWindow?.min}-${candidateWindow?.max} years)`);
  }
  if (!hardReq3) {
    improvements?.push(`Reduce median gap between measurements (currently ${Math.round(medianGap)} days, target ≤45 days)`);
  }
  if (!hardReq4) {
    improvements?.push(`Reduce suspicious intervals (currently ${Math.round(suspiciousPercentage * 100)}%, target ≤20%)`);
  }

  if (!meetsHardRequirements) {
    return {
      grade: 'D',
      meetsHardRequirements: false,
      confidence: 'insufficient',
      message: 'Insufficient data - PHV cannot be reported',
      improvements: improvements,
      hardRequirements: {
        minMeasurements: hardReq1,
        windowMeasurements: hardReq2,
        medianGap: hardReq3,
        suspiciousIntervals: hardReq4
      }
    };
  }

  // CONFIDENCE GRADING (A, B, C)
  // Grade A: High confidence
  const gradeAConditions = [
    measurements?.length >= 12,
    spanMonths >= 12,
    measurementsInWindow?.length >= 4,
    lowReplicateDiffs >= replicateMeasurements?.length * 0.7,
    suspiciousPercentage < 0.10
  ];

  if (gradeAConditions?.every(c => c)) {
    return {
      grade: 'A',
      meetsHardRequirements: true,
      confidence: 'high',
      message: 'Excellent data quality - PHV estimate is highly reliable',
      improvements: ['Data quality is excellent - continue regular measurements'],
      hardRequirements: {
        minMeasurements: true,
        windowMeasurements: true,
        medianGap: true,
        suspiciousIntervals: true
      }
    };
  }

  // Grade B: Moderate confidence
  const gradeBConditions = [
    measurements?.length >= 10,
    medianGap <= 35,
    suspiciousPercentage < 0.15
  ];

  if (gradeBConditions?.every(c => c)) {
    improvements?.push('Add more measurements for higher confidence');
    improvements?.push('Reduce gaps between measurements');
    return {
      grade: 'B',
      meetsHardRequirements: true,
      confidence: 'moderate',
      message: 'Good data quality - PHV estimate is moderately reliable',
      improvements: improvements,
      hardRequirements: {
        minMeasurements: true,
        windowMeasurements: true,
        medianGap: true,
        suspiciousIntervals: true
      }
    };
  }

  // Grade C: Low confidence
  improvements?.push('Add more frequent measurements to improve confidence');
  improvements?.push('Ensure consistent measurement technique');
  improvements?.push('Take replicate measurements to verify accuracy');
  
  return {
    grade: 'C',
    meetsHardRequirements: true,
    confidence: 'low',
    message: 'Minimum data quality met - PHV estimate has wider uncertainty',
    improvements: improvements,
    hardRequirements: {
      minMeasurements: true,
      windowMeasurements: true,
      medianGap: true,
      suspiciousIntervals: true
    }
  };
};

export const detectPHVWithConfidence = (velocityData, sexAtBirth, currentAge, confidenceGrade) => {
  const candidateWindow = sexAtBirth === 'female' 
    ? { min: 8, max: 16 } 
    : { min: 10, max: 18 };

  if (currentAge < candidateWindow?.min || currentAge > candidateWindow?.max) {
    return {
      status: 'not_assessed',
      message: `PHV detection not assessed for this age range. Assessment window: ${candidateWindow?.min}-${candidateWindow?.max} years for ${sexAtBirth}s.`,
      candidateWindow
    };
  }

  // GUARDRAIL: Never show PHV outputs for grade D
  if (confidenceGrade?.grade === 'D') {
    return {
      status: 'insufficient_data',
      message: 'Insufficient data - does not meet hard requirements for PHV reporting',
      candidateWindow
    };
  }

  const candidateVelocities = velocityData?.filter(
    v => v?.ageInYears >= candidateWindow?.min && v?.ageInYears <= candidateWindow?.max
  );

  if (candidateVelocities?.length < 3) {
    return {
      status: 'insufficient_data',
      message: 'Insufficient measurements within the candidate window for PHV detection.',
      candidateWindow
    };
  }

  const smoothedVelocities = simpleMovingAverage(candidateVelocities, 3);

  let maxVelocity = -Infinity;
  let phvAge = null;

  smoothedVelocities?.forEach(v => {
    if (v?.smoothedVelocity > maxVelocity) {
      maxVelocity = v?.smoothedVelocity;
      phvAge = v?.ageInYears;
    }
  });

  if (!phvAge) {
    return {
      status: 'not_detected',
      message: 'PHV could not be detected with current data quality.',
      candidateWindow
    };
  }

  // GUARDRAIL: Adjust uncertainty based on confidence grade - never show single date without range
  let uncertaintyYears = 0.5;
  if (confidenceGrade?.grade === 'C') {
    uncertaintyYears = 1.0; // Wider range for low confidence
  } else if (confidenceGrade?.grade === 'A') {
    uncertaintyYears = 0.3; // Narrower range for high confidence
  }

  const phvDate = new Date(new Date(currentAge).setFullYear(new Date().getFullYear() - (currentAge - phvAge)));

  // GUARDRAIL: Use cautious language for Grade C
  const messagePrefix = confidenceGrade?.grade === 'C' ?'PHV may be around' :'Peak height velocity detected at approximately';

  return {
    status: 'detected',
    phvAge: phvAge,
    phvAgeRange: {
      earliest: phvAge - uncertaintyYears,
      latest: phvAge + uncertaintyYears
    },
    peakVelocity: maxVelocity,
    confidence: confidenceGrade?.confidence,
    confidenceGrade: confidenceGrade?.grade,
    message: confidenceGrade?.grade === 'C' 
      ? `${messagePrefix} ${phvAge?.toFixed(1)} years (range: ${(phvAge - uncertaintyYears)?.toFixed(1)}-${(phvAge + uncertaintyYears)?.toFixed(1)} years). CAUTION: Interpret with care due to data quality.`
      : `${messagePrefix} ${phvAge?.toFixed(1)} years (range: ${(phvAge - uncertaintyYears)?.toFixed(1)}-${(phvAge + uncertaintyYears)?.toFixed(1)} years)`,
    candidateWindow
  };
};

export const calculateMaturityOffset = (standingHeightCm, sittingHeightCm, bodyMassKg, ageYears, sexAtBirth) => {
  if (!standingHeightCm || !bodyMassKg || !ageYears || !sexAtBirth) {
    return null;
  }

  // Mirwald et al. (2002) maturity offset equations
  let maturityOffset;

  if (sexAtBirth === 'female') {
    if (!sittingHeightCm) return null;
    const legLength = standingHeightCm - sittingHeightCm;
    maturityOffset = -9.376 + 
      (0.0001882 * legLength * sittingHeightCm) +
      (0.0022 * ageYears * legLength) +
      (0.005841 * ageYears * sittingHeightCm) -
      (0.002658 * ageYears * bodyMassKg) +
      (0.07693 * (bodyMassKg / standingHeightCm) * 100);
  } else {
    if (!sittingHeightCm) return null;
    const legLength = standingHeightCm - sittingHeightCm;
    maturityOffset = -9.236 + 
      (0.0002708 * legLength * sittingHeightCm) -
      (0.001663 * ageYears * legLength) +
      (0.007216 * ageYears * sittingHeightCm) +
      (0.02292 * (bodyMassKg / standingHeightCm) * 100);
  }

  return {
    yearsFromPHV: maturityOffset,
    estimatedPHVAge: ageYears - maturityOffset,
    range: {
      earliest: (ageYears - maturityOffset) - 1.0,
      latest: (ageYears - maturityOffset) + 1.0
    },
    note: 'Estimate only - not a measurement. Wide plausible range shown.'
  };
};

export const calculatePercentPredictedAdultHeight = (currentHeightCm, ageYears, sexAtBirth, parentalHeights) => {
  if (!currentHeightCm || !ageYears || !sexAtBirth) {
    return null;
  }

  // Khamis-Roche method (requires parental heights)
  if (!parentalHeights?.motherHeightCm || !parentalHeights?.fatherHeightCm) {
    return null;
  }

  const midParentHeight = (parseFloat(parentalHeights?.motherHeightCm) + parseFloat(parentalHeights?.fatherHeightCm)) / 2;
  
  // Simplified prediction (actual method uses age-specific coefficients)
  let predictedAdultHeight;
  if (sexAtBirth === 'female') {
    predictedAdultHeight = (midParentHeight - 6.5) * 0.5 + currentHeightCm * 0.5;
  } else {
    predictedAdultHeight = (midParentHeight + 6.5) * 0.5 + currentHeightCm * 0.5;
  }

  const percentPredicted = (currentHeightCm / predictedAdultHeight) * 100;

  return {
    percentPredicted: percentPredicted,
    predictedAdultHeight: predictedAdultHeight,
    range: {
      min: predictedAdultHeight - 5,
      max: predictedAdultHeight + 5
    },
    note: 'Estimate only - not a measurement. Based on parental heights.'
  };
};

export const classifyGrowthPhaseWithConfidence = (phvResult, currentAge, confidenceGrade) => {
  // GUARDRAIL: Never show phase for grade D
  if (confidenceGrade?.grade === 'D') {
    return {
      phase: 'unavailable',
      description: 'Phase not available yet',
      guidance: 'Continue adding measurements to meet minimum data requirements for phase classification',
      confidence: 'insufficient'
    };
  }

  if (phvResult?.status !== 'detected') {
    return {
      phase: 'unknown',
      description: 'Growth phase cannot be determined without PHV detection',
      guidance: 'Continue regular measurements to enable PHV detection',
      confidence: confidenceGrade?.confidence
    };
  }

  let phvAge = phvResult?.phvAge;
  const monthsFromPHV = (currentAge - phvAge) * 12;

  let phase, description, guidance;

  if (monthsFromPHV < -6) {
    phase = 'pre_rapid_growth';
    description = 'Pre-Rapid Growth';
    guidance = 'Focus on fundamental movement skills and varied activities. Avoid early specialisation. Emphasise enjoyment and skill development.';
  } else if (monthsFromPHV >= -6 && monthsFromPHV < -3) {
    phase = 'approaching_phv';
    description = 'Approaching PHV';
    guidance = 'Monitor training load carefully. Continue skill development. Be aware that rapid growth may begin soon.';
  } else if (monthsFromPHV >= -3 && monthsFromPHV <= 3) {
    phase = 'around_phv';
    description = 'Around PHV';
    guidance = 'Period of rapid growth. Prioritise movement quality and technique. Caution with load progression. Ensure adequate recovery and sleep. Monitor for persistent pain.';
  } else {
    phase = 'post_phv';
    description = 'Post-PHV';
    guidance = 'Growth rate declining. Gradual progression of training load appropriate. Continue emphasis on movement quality and strength development.';
  }

  // GUARDRAIL: Use cautious language for Grade C - avoid definitive statements
  if (confidenceGrade?.grade === 'C') {
    description = `Possible ${description}`;
    guidance = `${guidance} NOTE: Phase classification has wider uncertainty due to data quality. The child may be at a different phase.`;
  }

  return {
    phase: phase,
    description: description,
    guidance: guidance,
    confidence: confidenceGrade?.confidence,
    monthsFromPHV: monthsFromPHV
  };
};

export const classifyGrowthPhase = (phvResult, currentAge) => {
  if (phvResult?.status !== 'detected') {
    return {
      phase: 'unknown',
      description: 'Growth phase cannot be determined without PHV detection',
      guidance: 'Continue regular measurements to enable PHV detection'
    };
  }

  let phvAge = phvResult?.phvAge;
  const timeSincePHV = currentAge - phvAge;

  if (timeSincePHV < -1) {
    return {
      phase: 'pre_phv',
      description: 'Pre-Peak Height Velocity',
      guidance: 'Focus on fundamental movement skills, varied activities, and enjoyment. Avoid early specialisation.'
    };
  }

  if (timeSincePHV >= -1 && timeSincePHV <= 1) {
    return {
      phase: 'around_phv',
      description: 'Around Peak Height Velocity',
      guidance: 'Period of rapid growth. Monitor training load carefully. Prioritise technique and movement quality over intensity.'
    };
  }

  if (timeSincePHV > 1 && timeSincePHV <= 3) {
    return {
      phase: 'post_phv_early',
      description: 'Early Post-Peak Height Velocity',
      guidance: 'Growth rate slowing. Gradual increase in training volume and intensity appropriate. Continue focus on skill development.'
    };
  }

  return {
    phase: 'post_phv_late',
    description: 'Late Post-Peak Height Velocity',
    guidance: 'Approaching adult height. Training can progress towards adult-like volumes and intensities based on individual readiness.'
  };
};